<template>
  <div class="modal-container" data-testid="edit-request-content-modal-wrapper">
    <div class="modal-inner">
      <div class="slider-overflow" :class="slideClass">
        <!-- Form slide -->
        <div class="slide-container">
          <div class="modal-header">
            <h2 class="modal-title">{{ MODAL_TITLES.EDIT_REQUEST_LINK }}</h2>
          </div>

          <RequestContentForm
            v-if="currentView === MODAL_VIEWS.FORM"
            :edit="true"
            :item="item"
            :domain="domain"
            :selected-collection="selectedCollection"
            @save="handleEditFormSave"
            @close="onCloseHandler"
            @show-collection-picker="openCollectionPicker"
            @edit-url="showEditUrl"
          />
        </div>

        <!-- Success slide -->
        <div class="slide-container">
          <RequestContentSuccess
            v-if="currentView === MODAL_VIEWS.SUCCESS && savedItem"
            :item="savedItem"
            :domain="domain"
            @close="onCloseHandler"
            @edit-url="showEditUrl"
          />
        </div>

        <!-- Edit URL slide -->
        <div class="slide-container">
          <RequestContentEditUrl
            v-if="currentView === MODAL_VIEWS.EDIT_URL && savedItem"
            :item="savedItem"
            :domain="domain"
            @close="showPreviousView"
            @save="handleUrlSaveOverride"
          />
        </div>

        <!-- Collection picker slide -->
        <div class="slide-container">
          <CollectionPicker
            v-if="showCollectionPicker"
            :selected-id="selectedCollectionId"
            :exclude-secure="false"
            @select="handleCollectionSelect"
            @cancel="closeCollectionPicker"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { RequestContent } from '../../types';
import CollectionPicker from '@/components/CollectionPicker.vue';
import RequestContentForm from './RequestContentForm.vue';
import RequestContentSuccess from './RequestContentSuccess.vue';
import RequestContentEditUrl from './RequestContentEditUrl.vue';
import { useRequestContentModalShared } from '../../composables/useRequestContentModalShared';
import { useRequestContentStore } from '@/store/useRequestContentStore';
import { useToast } from '@/composables/useToast';
import { MODAL_VIEWS, MODAL_TITLES } from '../../constants/constants';

interface Props {
  item: RequestContent;
  domain: string;
  onClose?: () => void;
  onSave?: (item: RequestContent) => void;
}

const props = defineProps<Props>();

interface Emits {
  close: [];
  save: [item: RequestContent];
}

const emit = defineEmits<Emits>();

const requestContentStore = useRequestContentStore();
const { error } = useToast();

// Use shared modal logic
const {
  currentView,
  savedItem,
  showCollectionPicker,
  selectedCollectionId,
  selectedCollection,
  slideClass,
  onCloseHandler,
  showEditUrl,
  showPreviousView,
  handleCollectionSelect,
  closeCollectionPicker,
  openCollectionPicker,
} = useRequestContentModalShared({
  item: props.item,
  onClose: () => emit('close'),
});

const handleEditFormSave = async (formData: any) => {
  try {
    // Call store function directly with frontend form data
    const updatedItem = await requestContentStore.updateRequestContent(props.item.id, formData);
    savedItem.value = updatedItem;

    emit('save', updatedItem);
    currentView.value = MODAL_VIEWS.SUCCESS;
  } catch (apiError) {
    console.error('Failed to update request content:', apiError);
    error('Failed to update request link. Please try again.');
  }
};

// Override handleUrlSave to show success view after URL update
const handleUrlSaveOverride = async (item: RequestContent) => {
  // Update the saved item with the new URL data
  savedItem.value = item;

  // Show success view instead of closing immediately
  currentView.value = MODAL_VIEWS.SUCCESS;

  // Emit save event to update parent component
  emit('save', item);
};
</script>

<style scoped>
.modal-container {
  @apply w-full md:w-128 max-w-2xl pt-16 sm:pt-5;
}

.modal-inner {
  @apply overflow-hidden;
}

.slider-overflow {
  @apply flex flex-row transition-all duration-300 ease-in-out;
  width: 400%; /* 4 slides: form, success, edit-url, collection-picker */
  transform: translateX(0);
  min-height: 320px; /* Reduced height for better fit across slides */
  position: relative;
}

.slide-container {
  @apply px-5;
  width: 25%; /* 1/4 of the total width */
  flex-shrink: 0;
  min-height: inherit; /* Inherit min-height from parent */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

/* Slide positions */
.slide-success {
  transform: translateX(-25%); /* Move to second slide */
}

.slide-edit-url {
  transform: translateX(-50%); /* Move to third slide */
}

.slide-collection {
  transform: translateX(-75%); /* Move to fourth slide */
}

.modal-header {
  @apply flex items-center justify-between mb-6 border-b border-zinc-200 dark:border-zinc-700 pb-4 pr-12;
}

.modal-title {
  @apply text-xl font-semibold text-zinc-900 dark:text-zinc-100;
}
</style>
